import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, ChevronDown, ChevronUp, Download, Info, MessageCircle, Scale, Sparkles } from 'lucide-react';
import { useToast, AsyncBoundary } from '@kollez/design-system';
import { useOffers, useApplicationRun, useMarkNotifReadBySubject } from '@/lib/queries';
import { useAuthStore } from '@/lib/authStore';
import { apiPost } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';

/**
 * /offers/:id (F84a) — direct port of kollez/pages/student/offer-detail.html:
 *   Inkwell hero + countdown → What you'll earn (.ctc dl) → Before you decide → letter accordion
 *   → sticky decide grid → accept-confirm 3-ack view → decline reason view → done timeline + 10-min undo
 */

type View = 'offer' | 'confirm' | 'decline' | 'done';

const DECLINE_REASONS = [
  'I\'m going for higher studies',
  'I have a better offer',
  'The location or bond doesn\'t work for me',
  'Something else',
];

export function OfferDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useOffers();
  const toast = useToast();
  const { user } = useAuthStore();

  const offer = data?.offers.find((o) => o._id === id);
  const runQ = useApplicationRun(offer?.application);
  const run = runQ.data?.run ?? null;

  const [view, setView] = useState<View>('offer');
  const [letterOpen, setLetterOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);

  // Step 54 — landing on this page counts as "reading" any pending offer notifications
  // (release / stage-advance / etc. whose subject={kind:'Offer',id}). Fires once per (id,
  // mount) — no need to wait for `offer` to load since the URL id IS the subject. Guarded
  // via a ref so React StrictMode's dev double-invoke doesn't double-fire. Non-fatal: a
  // failed mutation just leaves the pill until the 30s refetch cycle catches up.
  const markRead = useMarkNotifReadBySubject();
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!id || firedFor.current === id) return;
    firedFor.current = id;
    void markRead.mutateAsync({ kind: 'Offer', id }).catch(() => {
      // Non-fatal — refetch cycle catches the discrepancy.
    });
  }, [id, markRead]);

  // accept flow
  const [acks, setAcks] = useState<[boolean, boolean, boolean]>([false, false, false]);
  const [signed, setSigned] = useState('');

  // decline flow
  const [reason, setReason] = useState<string | null>(null);
  const [declineNote, setDeclineNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [undoLeft, setUndoLeft] = useState(0);

  const partnerName = (offer as { partner?: { name?: string } } | undefined)?.partner?.name ?? 'The company';
  const role = (offer as { role?: string } | undefined)?.role ?? 'a role';
  const ctc = (offer as { ctc?: number } | undefined)?.ctc ?? 0;
  const deadline = (offer as { deadline?: string } | undefined)?.deadline;

  const cd = useMemo(() => {
    if (!deadline) return null;
    const s = Math.max(0, (new Date(deadline).getTime() - now) / 1000);
    return {
      days: Math.floor(s / 86400),
      hours: Math.floor((s % 86400) / 3600),
      mins: Math.floor((s % 3600) / 60),
    };
  }, [deadline, now]);

  const canAccept = acks.every(Boolean) && signed.trim().length > 3;

  async function doAccept() {
    setSubmitting(true);
    try {
      await apiPost(`/offers/${id}/accept`, { acks, signedName: signed });
      toast.show({ message: 'You\'re joining ' + partnerName });
      setView('done');
      setUndoLeft(600);
      // v1.6 §7.10 — badge issuance runs async on the server after Offer.accept (Step 15
      // fires-and-forgets issueBadgeForStage). Give it a moment to land, then refetch the
      // run so the "You just earned a credential" card can render the emittedBadge. Two
      // retries because badge write is non-atomic with offer.accept and the first refetch
      // may race the DB write.
      void (async () => {
        for (const delay of [400, 1200]) {
          await new Promise((r) => setTimeout(r, delay));
          const res = await runQ.refetch();
          const emitted = res.data?.run?.history?.some(
            (h) => typeof h.emittedBadge === 'object' && h.emittedBadge !== null,
          );
          if (emitted) break;
        }
      })();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Accept failed';
      toast.show({ tone: 'warning', message: msg });
    } finally { setSubmitting(false); }
  }

  async function doDecline() {
    if (!reason) return;
    setSubmitting(true);
    try {
      await apiPost(`/offers/${id}/decline`, { reason, note: declineNote });
      toast.show({ message: `You declined the ${partnerName} offer.` });
      setView('offer');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Decline failed';
      toast.show({ tone: 'warning', message: msg });
    } finally { setSubmitting(false); }
  }

  async function doUndo() {
    try {
      await apiPost(`/offers/${id}/undo`);
      toast.show({ message: 'Acceptance undone. Your applications are back as they were.' });
      setView('offer');
      setUndoLeft(0);
    } catch { toast.show({ tone: 'warning', message: 'Undo window has passed.' }); }
  }

  // 10-min countdown after accept.
  // Step 79 — earlier the dep array included `undoLeft`, so this effect tore down and
  // recreated the setInterval on every tick. Correct behavior, wasted work. The setter
  // uses the functional form `(n) => ...` so it doesn't need to close over the current
  // value — the effect can safely depend only on `view`. When view becomes 'done' the
  // interval starts; when it leaves 'done' cleanup fires and the interval stops.
  useEffect(() => {
    if (view !== 'done') return;
    const t = setInterval(() => setUndoLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(t);
  }, [view]);

  return (
    <AsyncBoundary
      kind="detail"
      loading={isLoading}
      error={error}
      empty={!isLoading && !offer}
      emptyTitle="Offer not found"
      onRetry={() => void refetch()}
    >
      {offer && (
        <>
          <style>{`
            .ctc { display: grid; grid-template-columns: 1fr auto; gap: 8px 16px; font-size: .9375rem; margin: 0; }
            .ctc dd { margin: 0; text-align: right; }
            .ctc .tot { border-top: 1px solid rgb(var(--line-secondary)); padding-top: 8px; font-weight: 600; }
            .countdown { display: flex; gap: 6px; }
            .countdown div { min-width: 52px; text-align: center; padding: 6px 4px; border-radius: 8px; background: rgb(255 255 255 / .1); }
            .countdown b { display: block; font-family: "Newsreader", Georgia, serif; font-size: 1.5rem; font-weight: 500; line-height: 1.1; }
            .countdown span { font-size: .6875rem; opacity: .8; }
            .decide { display: grid; gap: 8px; grid-template-columns: 1fr 1fr; }
          `}</style>

          {view === 'offer' && (
            <div className="stack" style={{ paddingBottom: 120 }}>
              {/* Hero */}
              <section className="card inkwell card-pad grid gap-4">
                <div className="flex items-center gap-3">
                  <span className="logo-tile" style={{ background: '#fff', color: '#152252' }}>{partnerName.slice(0, 2).toUpperCase()}</span>
                  <div>
                    <p className="m-0 text-sm opacity-80">Offer from {partnerName}</p>
                    <h1 className="h-serif text-[1.75rem] leading-tight m-0">{role}</h1>
                  </div>
                </div>
                {cd && (
                  <div>
                    <p className="m-0 text-sm opacity-80">Reply before {deadline ? formatIndianDate(deadline) + ', 11:59 pm' : 'the deadline'}</p>
                    <div className="countdown mt-2">
                      <div><b>{cd.days}</b><span>days</span></div>
                      <div><b>{cd.hours}</b><span>hours</span></div>
                      <div><b>{cd.mins}</b><span>mins</span></div>
                    </div>
                  </div>
                )}
              </section>

              {run ? (
                <section className="card card-pad grid gap-3">
                  <div className="flex items-baseline justify-between">
                    <h2 className="h-section">Your run so far</h2>
                    <span className="cell-sub">
                      {typeof run.workflow === 'object' && run.workflow ? run.workflow.name : 'template'}
                      {' · v'}{run.workflowVersion}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="tag tag-pressed">current: {run.currentStage.replace(/_/g, ' ')}</span>
                    {run.autoSatisfiedStages.length > 0 ? (
                      <span className="tag" title={run.autoSatisfiedStages.join(', ')}>
                        skipped {run.autoSatisfiedStages.length} via badges
                      </span>
                    ) : null}
                    <span className={`badge ${run.status === 'active' ? 'badge-success' : run.status === 'flagged' ? 'badge-warning' : ''}`}>
                      {run.status}
                    </span>
                  </div>

                  {/* v1.6 §7.10 — surface which badges satisfied which stages so the student
                      sees how their earned credentials worked in their favour. Step 36 populates
                      history[i].satisfiedByBadge as the full Badge projection. */}
                  {(() => {
                    const satisfies = run.history
                      .filter((h) => typeof h.satisfiedByBadge === 'object' && h.satisfiedByBadge !== null)
                      .map((h) => ({
                        stage: h.fromStage,
                        badge: h.satisfiedByBadge as Exclude<typeof h.satisfiedByBadge, string | undefined>,
                      }));
                    const emits = run.history
                      .filter((h) => typeof h.emittedBadge === 'object' && h.emittedBadge !== null)
                      .map((h) => ({
                        stage: h.toStage,
                        badge: h.emittedBadge as Exclude<typeof h.emittedBadge, string | undefined>,
                      }));
                    if (satisfies.length === 0 && emits.length === 0) return null;
                    return (
                      <div className="grid gap-1 text-sm">
                        {satisfies.map(({ stage, badge }, i) => (
                          <div key={`s-${i}`} className="cell-sub">
                            ✓ <b>{stage}</b> skipped — your <b>{badge.skill}</b>
                            {badge.level ? ` (${badge.level})` : ''} badge from{' '}
                            {badge.issuer?.name ?? 'a trusted issuer'} covered it.
                          </div>
                        ))}
                        {emits.map(({ stage, badge }, i) => (
                          <div key={`e-${i}`} className="cell-sub">
                            🏅 <b>{stage}</b> earned you the <b>{badge.skill}</b>
                            {badge.level ? ` (${badge.level})` : ''} badge
                            {badge.certified ? ' · certified' : ''}. It travels with you.
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  <p className="helper m-0">
                    Every stage has an author, a reversibility class, and sometimes a badge.
                    Accepting this offer completes the run.
                  </p>
                </section>
              ) : null}

              {/* Pay — deep-audit fix (Page 18/N): removed the fabricated
                  90/6/4% Fixed/Performance/Joining split. The API returns
                  ctc as a scalar; that split was invented client-side and
                  presented as authoritative offer terms — a fabricated
                  aggregate. Same for the after-tax in-hand estimate. Only
                  the real number (ctc) is shown; the letter is the truth
                  of record for the breakdown. */}
              <section className="card card-pad grid gap-4">
                <div className="flex items-baseline justify-between">
                  <h2 className="h-section">What you'll earn</h2>
                  <span className="h-serif text-2xl">{ctc ? `₹${(ctc / 100000).toFixed(1)} LPA` : '—'}</span>
                </div>
                <dl className="ctc">
                  <dt>Cost to company (annual)</dt><dd className="tot">₹{ctc.toLocaleString('en-IN')}</dd>
                </dl>
                <p className="helper m-0">
                  Read the offer letter below for the breakdown of fixed pay, variable, and joining components.
                </p>
              </section>

              {/* Terms */}
              <section className="card card-pad grid gap-3">
                <h2 className="h-section">Before you decide</h2>
                <dl className="dl">
                  <dt>Joining</dt>
                  <dd>{formatOfferJoining((offer as { joiningDate?: string }).joiningDate)}</dd>
                  <dt>Location</dt>
                  <dd>{(offer as { location?: string }).location ?? 'To be assigned'}</dd>
                  <dt>Training</dt>
                  <dd>{formatOfferTraining((offer as { trainingWeeks?: number; training?: string }).trainingWeeks, (offer as { training?: string }).training)}</dd>
                  <dt>Service agreement</dt>
                  {/* F-091: bond is {months, amountInr}, not a string. Render the shape safely. */}
                  <dd>{formatOfferBond((offer as { bond?: { months?: number; amountInr?: number } }).bond)}</dd>
                </dl>
                <div className="notice notice-warning">
                  <Scale size={14} />
                  <div><b>Accepting ends your Day 1 and Day 2 placements.</b> Day 0 drives you're already in stay on; the rest are withdrawn.</div>
                </div>
              </section>

              {/* Letter accordion — deep-audit fix (Page 18/N): removed
                  the fabricated 'Ref /NTH/26/1187' number and the hardcoded
                  '3 pages' count. The preview now uses the student's real
                  name (not the literal placeholder 'Candidate') and calls
                  itself out as a preview so no one confuses it with the
                  signed letter. */}
              <section className="card">
                <button className="card-head w-full text-left" onClick={() => setLetterOpen((v) => !v)} aria-expanded={letterOpen}>
                  <h2 className="h-section">Offer letter</h2>
                  <span className="flex items-center gap-2 text-sm text-ink-secondary">
                    {letterOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </span>
                </button>
                {letterOpen && (
                  <div className="p-3 sm:p-5 grid gap-3">
                    <div className="paper" style={{ padding: '28px 22px 28px 44px', fontSize: 14.5 }}>
                      <p style={{ color: '#5b6070', fontSize: 13 }}>{partnerName} · preview</p>
                      <p>Dear <span className="var">{user?.fullName ?? 'Candidate'}</span>,</p>
                      <p>We are pleased to offer you the position of <span className="var">{role}</span>, with an annual cost to company of <span className="var">₹{ctc.toLocaleString('en-IN')}</span>…</p>
                      <p className="helper m-0" style={{ marginTop: 12 }}>
                        This is a preview. The signed letter is the truth of record — download it below.
                      </p>
                    </div>
                    {offer.letterUrl && (
                      <a className="btn btn-outline justify-self-start" href={offer.letterUrl} target="_blank" rel="noopener noreferrer">
                        <Download size={14} />Download signed PDF
                      </a>
                    )}
                  </div>
                )}
              </section>

              {/* Not sure? */}
              <section className="card card-pad grid gap-3">
                <h2 className="h-section">Not sure?</h2>
                <div className="flex flex-wrap gap-2">
                  <Link to="/nexie" className="btn btn-outline btn-sm"><Sparkles size={14} />Compare with my other chances</Link>
                  <Link to="/nexie" className="btn btn-outline btn-sm"><MessageCircle size={14} />Message the placement office</Link>
                </div>
              </section>

              {/* Sticky decide */}
              <div className="decide" style={{ position: 'sticky', bottom: 0, background: 'rgb(var(--bg-secondary))', padding: '12px 0' }}>
                <button className="btn btn-outline btn-lg" onClick={() => setView('decline')}>Decline</button>
                <button className="btn btn-primary btn-lg" onClick={() => setView('confirm')}>Accept offer</button>
              </div>
            </div>
          )}

          {view === 'confirm' && (
            <div className="stack">
              <button className="btn btn-ghost justify-self-start" onClick={() => setView('offer')}><ArrowLeft size={14} />Back to offer</button>
              <section className="card card-pad grid gap-4">
                <h1 className="h-page" style={{ fontSize: '1.75rem' }}>Accept {partnerName}'s offer?</h1>
                <p className="subhead">Check each one so you know what happens next.</p>
                {[
                  'I\'ve read and understood the terms and service agreement.',
                  `My other active applications for the same tier will be withdrawn.`,
                  'I can still attend Day 0 drives already scheduled. Switching later needs placement office approval.',
                ].map((label, i) => (
                  <label key={i} className="flex gap-3 items-start">
                    <input
                      type="checkbox"
                      className="check mt-1"
                      checked={acks[i]}
                      onChange={(e) => setAcks((prev) => { const n = [...prev] as typeof prev; n[i] = e.target.checked; return n; })}
                    />
                    <span>{label}</span>
                  </label>
                ))}
                <div className="grid gap-1.5">
                  <span className="label">Type your name to sign</span>
                  <input className="input" value={signed} onChange={(e) => setSigned(e.target.value)} autoComplete="name" />
                </div>
                <button className="btn btn-primary btn-lg" disabled={!canAccept || submitting} onClick={doAccept}>
                  {submitting ? 'Accepting…' : 'Accept and sign'}
                </button>
              </section>
            </div>
          )}

          {view === 'decline' && (
            <div className="stack">
              <button className="btn btn-ghost justify-self-start" onClick={() => setView('offer')}><ArrowLeft size={14} />Back to offer</button>
              <section className="card card-pad grid gap-4">
                <h1 className="h-page" style={{ fontSize: '1.75rem' }}>Decline this offer</h1>
                <p className="subhead">{partnerName} and your placement office will see your reason. You can't undo this after 10 minutes.</p>
                <div className="grid gap-2">
                  {DECLINE_REASONS.map((r) => (
                    <label key={r} className="flex gap-2 items-center">
                      <input type="radio" name="r" className="check" checked={reason === r} onChange={() => setReason(r)} />
                      {r}
                    </label>
                  ))}
                </div>
                <textarea
                  className="textarea"
                  rows={3}
                  placeholder="Anything you'd like to add (optional)"
                  value={declineNote}
                  onChange={(e) => setDeclineNote(e.target.value)}
                />
                <button className="btn btn-danger btn-lg" disabled={!reason || submitting} onClick={doDecline}>
                  {submitting ? 'Declining…' : 'Decline offer'}
                </button>
              </section>
            </div>
          )}

          {view === 'done' && (
            <div className="stack">
              <section className="card card-pad grid gap-4 justify-items-start">
                <span className="logo-tile" style={{ width: 48, height: 48, color: 'rgb(var(--ink-success))', borderColor: 'rgb(var(--ink-success))' }}>
                  <BadgeCheck size={22} />
                </span>
                <h1 className="h-page" style={{ fontSize: '2rem' }}>You're joining {partnerName}.</h1>
                <p className="subhead">We've told {partnerName} and your placement office. Your signed letter is in your email.</p>
                <ol className="timeline w-full mt-2">
                  <li data-state="done"><span className="t-dot" /><div className="t-title">Offer accepted</div><div className="t-meta">Just now</div></li>
                  <li data-state="current"><span className="t-dot" /><div className="t-title">Day 0 interviews still on</div><div className="t-meta">Your calendar has them</div></li>
                  <li><span className="t-dot" /><div className="t-title">Document verification</div><div className="t-meta">After final results</div></li>
                  <li><span className="t-dot" /><div className="t-title">Joining</div><div className="t-meta">See offer letter for date</div></li>
                </ol>
                <div className="flex gap-2 flex-wrap">
                  <Link className="btn btn-primary" to="/home">Back to home</Link>
                  {undoLeft > 0 && (
                    <button className="btn btn-ghost" onClick={doUndo}>
                      <Info size={14} />Changed your mind? Undo ({Math.floor(undoLeft / 60)}:{String(undoLeft % 60).padStart(2, '0')} left)
                    </button>
                  )}
                </div>
              </section>

              {/* v1.6 §7.10 — badge that traveled with the acceptance. Reads the most recent
                  populated emittedBadge from run.history. Step 15 issues the badge server-side
                  on Offer.accept; Step 36 populates history[i].emittedBadge on the API read.
                  Silent when no badge was emitted (older runs / stages without emitsBadge). */}
              {(() => {
                if (!run) return null;
                const emit = [...run.history]
                  .reverse()
                  .find((h) => typeof h.emittedBadge === 'object' && h.emittedBadge !== null)
                  ?.emittedBadge as Exclude<typeof run.history[number]['emittedBadge'], string | undefined> | undefined;
                if (!emit) return null;
                return (
                  <section className="card card-pad grid gap-3">
                    <div className="flex items-baseline justify-between">
                      <h2 className="h-section">You just earned a credential</h2>
                      <span className="cell-sub">portable across every future application</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="tag tag-pressed" style={{ fontSize: 13 }}>
                        {emit.skill}{emit.level ? ` · ${emit.level}` : ''}
                      </span>
                      {emit.certified ? <span className="badge badge-success">certified</span> : null}
                      {emit.verified ? <span className="badge">verified</span> : null}
                      {emit.issuerTier ? (
                        <span className="cell-sub">
                          issued at {emit.issuerTier.replace('_', ' ')}
                          {emit.issuer?.name ? ` by ${emit.issuer.name}` : ''}
                        </span>
                      ) : null}
                    </div>
                    <p className="helper m-0">
                      Kollez badges travel with you. Next time you apply somewhere, any workflow that
                      asks for this skill will already know you have it — no re-test.
                    </p>
                    <Link className="btn btn-ghost btn-sm justify-self-start" to="/profile">
                      See all your badges →
                    </Link>
                  </section>
                );
              })()}
            </div>
          )}
        </>
      )}
    </AsyncBoundary>
  );
}

// F-091: helpers to safely render optional/object fields that the offer payload can carry.
function formatOfferJoining(joiningDate?: string): string {
  if (!joiningDate) return 'After your final results';
  try {
    const d = new Date(joiningDate);
    if (isNaN(d.getTime())) return joiningDate;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return joiningDate;
  }
}

function formatOfferTraining(trainingWeeks?: number, training?: string): string {
  if (trainingWeeks && trainingWeeks > 0) return `${trainingWeeks}-week paid onboarding`;
  return training ?? 'Paid onboarding';
}

function formatOfferBond(bond?: { months?: number; amountInr?: number }): string {
  if (!bond) return 'See offer letter';
  const parts: string[] = [];
  if (bond.months) parts.push(`${bond.months} months`);
  if (bond.amountInr) parts.push(`₹${bond.amountInr.toLocaleString('en-IN')} if broken`);
  return parts.length > 0 ? parts.join(' · ') : 'See offer letter';
}
